"use client";

import Heading from "@afa-shared/afa-components/dist/Heading";
import { StandardLink } from "@components/Link/StandardLink";
import LinkedHeading from "@components/LinkedHeading/LinkedHeading";
import RichTextRenderer from "@components/RichTextRenderer";
import CFContentArea from "@components/cFContentArea";
import Grid from "@components/client/Grid";
import { isColoredSection } from "@utils/layoutHelpers/layoutMarginHelper";
import { getInternalOrExternalIcon } from "@utils/linkIconHelper";
import { getBackgroundColor } from "@utils/pageHelpers/getBackgrundColor";
import { pixelsToRem } from "@utils/sizeHelper";
import { isNullOrEmpty } from "@utils/string";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { CookieConsentCategory, useCookieConsent } from "hooks/useCookieConsent";
import { useWasSeen } from "hooks/useWasSeen";
import { Context } from "types/context/Context";
import { HeadingContext } from "types/context/HeadingContext";
import { SectionBlockContext } from "types/context/SectionBlockContext";

import { SectionBlockType_sectionblock } from "./queries/contentfulTypes/SectionBlockType";
import sectionStyles from "./section.module.css";
import { sectionImpressionTracker } from "./sectionTracker";

interface ISectionBlockProps {
  blockData: SectionBlockType_sectionblock;
  uniqueKey?: string;
  context: Context;
  maxWidth?: number;
}

export const SectionBlock = ({
  blockData: sectionblock,
  context,
  maxWidth,
  uniqueKey,
}: ISectionBlockProps) => {
  //TODO: handle small images
  const sectionBlockContext: SectionBlockContext = {
    noHeading: !sectionblock?.showHeading,
    noImage: !sectionblock?.showImage,
    noText: !sectionblock?.showText,
    noLink: !sectionblock?.showLinks,
    coloredSection: isColoredSection(sectionblock?.backgroundColor) ? true : false,
    backgroundColor: sectionblock?.backgroundColor,
    smallImages: sectionblock?.smallImages,
    sectionBlockIndex: context.sectionBlockIndex ?? undefined,
    sectionBlockHeading: sectionblock?.heading ?? context?.pageHeading ?? undefined,
  };
  const headingContext: HeadingContext = {
    parentLevel: context?.heading?.parentLevel,
    childLevel:
      sectionblock?.heading && sectionblock?.showHeading
        ? context?.heading?.childLevel
        : context?.heading?.parentLevel,
  };
  context = {
    ...context,
    sectionBlock: sectionBlockContext,
    columns: sectionblock?.columns,
    heading: headingContext,
  };

  const backgroundColor = getBackgroundColor(sectionblock?.backgroundColor);
  const fallbackId = useId();
  const sectionId = useMemo(() => {
    if (uniqueKey) {
      return `section-${uniqueKey}`;
    }
    return fallbackId.replace(/:/g, "");
  }, [fallbackId, uniqueKey]);
  const necessaryCookieConsentGiven = useCookieConsent(CookieConsentCategory.necessary);
  const sectionRef = useRef(null);
  const [wasSeen, setWasSeen] = useState(false);
  useWasSeen(sectionRef, { threshold: 0.5 }, () => setWasSeen(true));
  useEffect(() => {
    if (!necessaryCookieConsentGiven || !wasSeen) return;
    sectionImpressionTracker(
      sectionblock?.heading || context?.pageHeading || "",
      context.sectionBlockIndex,
    );
  }, [wasSeen, necessaryCookieConsentGiven, context.sectionBlockIndex, sectionblock.heading]);

  useEffect(() => {
    if (!sectionblock?.isWide) return;
    const details = navigator.userAgent;
    const regexp = /android|iphone|ipad/i;
    const isMobileDevice = regexp.test(details);

    if (!isMobileDevice && document !== undefined) {
      const scrollbarOffset = window?.innerWidth - document?.body?.clientWidth;

      const calculatedPadding = pixelsToRem(48 + scrollbarOffset / 2);
      document
        ?.getElementById(sectionId)
        ?.style?.setProperty("--desktop-padding", calculatedPadding);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headingDirectionClass =
    sectionblock?.contentDirection === "Centrerat"
      ? sectionStyles.alignCenter
      : sectionblock?.contentDirection === "Höger"
        ? sectionStyles.alignEnd
        : "";

  const url = sectionblock.initialLink?.url;
  const globalLink = sectionblock.initialLink?.globalLinkItem;
  const internalReference = sectionblock.initialLink?.reference;
  const linkType = getInternalOrExternalIcon(url, internalReference, globalLink);
  const linkText = sectionblock?.initialLink?.linkText;
  const isWhiteSection = isNullOrEmpty(backgroundColor) || sectionblock?.backgroundColor === "Vit";

  const hasPreambleOrLink = sectionblock?.initialPreamble?.json || sectionblock?.initialLink?.url;
  const hasInitialInfo = (sectionblock?.heading && sectionblock?.showHeading) || hasPreambleOrLink;

  return (
    <Grid
      className={`${sectionStyles.section} ${sectionblock?.isWide ? sectionStyles.isWide : ""} ${
        isWhiteSection ? "" : sectionStyles.backgroundColor
      } ${maxWidth >= 1240 ? sectionStyles.widePage : ""}`}
      container
      justifyContent="center"
      alignItems="center"
      backgroundColor={backgroundColor}
      id={sectionId}
      variant={"section"}
      data-contentful-entry-id={sectionblock?.sys?.id}
      data-contentful-field-id="name"
      ref={sectionRef}
    >
      <Grid
        container
        maxWidth={pixelsToRem(1240)}
        backgroundColor={backgroundColor}
        className={sectionStyles.container}
      >
        <Grid
          className={`${sectionStyles.sectionWrapper} ${
            isWhiteSection ? "" : sectionStyles.hasBackground
          }`}
          container
          backgroundColor={backgroundColor}
          desktop={maxWidth === 1240 ? 12 : sectionblock?.isWide ? 8 : 12}
          margin={"0 auto"}
        >
          {hasInitialInfo && (
            <div
              className={`${sectionStyles.initialInfoWrapper} ${
                hasPreambleOrLink ? sectionStyles.hasPreambleOrLink : ""
              }`}
            >
              {sectionblock?.heading && sectionblock?.showHeading && (
                <div className={`${sectionStyles.headingWrapper} ${headingDirectionClass}`}>
                  {sectionblock?.linkOnSection ? (
                    <LinkedHeading
                      url={sectionblock?.linkOnSection?.url}
                      headingLevel={context?.heading?.parentLevel}
                      heading={sectionblock?.heading}
                    />
                  ) : (
                    <Heading variant={context?.heading?.parentLevel} removeMargin>
                      {sectionblock?.heading}
                    </Heading>
                  )}
                </div>
              )}
              {sectionblock?.initialPreamble?.json && (
                <RichTextRenderer
                  json={sectionblock.initialPreamble.json}
                  links={sectionblock.initialPreamble.links}
                  context={context}
                />
              )}
              {sectionblock.initialLink && (
                <StandardLink
                  type={linkType}
                  text={linkText}
                  href={url}
                  trackingData={{
                    sectionTitle: sectionblock?.heading ?? context?.pageHeading,
                    sectionBlockIndex: context.sectionBlockIndex,
                  }}
                />
              )}
            </div>
          )}
          <CFContentArea
            mobileColumns={1}
            tabletColumns={1}
            notebookColumns={sectionblock?.columns}
            desktopColumns={sectionblock?.columns}
            contentAreaItems={sectionblock?.columnAreaCollection?.items}
            context={context}
            heading={context?.heading?.childLevel}
            maxWidth={maxWidth}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SectionBlock;
